import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  Check,
  ArrowRight,
  ArrowLeft,
  AlertCircle,
  RefreshCw,
  Tag,
  Users,
  Plus,
  Minus,
  CheckCircle2,
  Lock,
  Star,
} from "lucide-react";
import {
  fetchSubscriptionPlans,
  startPartnerTrial,
  createPartnerSubscription,
  applyPartnerSubscriptionCoupon,
} from "../../api/partner/partnerApi";
import {
  setSubscriptionPlansList,
  setLoadingSubscription,
  setSubscriptionData,
  updatePartnerProfileField,
} from "../../features/partnerOnboarding/partnerOnboardingSlice";
import { showToast } from "../../components/common/notification/Notification";
import PartnerLayout from "../../components/partner/PartnerLayout";
import "./PartnerPages.css";

const DEFAULT_PARTNER_PLANS = [
  {
    id: 1,
    name: "Growth Monthly Plan",
    plan_name: "Growth Monthly Plan",
    price: 2999,
    base_price: 2999,
    total_price: 3538,
    duration_in_days: 30,
    billing_cycle: "MONTHLY",
    cycle_label: "1 Month",
    offer_text: "Perfect for growing teams",
    offer_badge_class: "partner-plan-offer--blue",
    trial_days: 15,
    max_users: 10,
    features: [
      "Verified Buyer & Tenant Lead Inquiries",
      "High Visibility on All Home Desire Catalog",
      "Official Partner Trust Badge",
      "Dedicated Partner CRM & Inquiry Management",
    ],
  },
  {
    id: 2,
    name: "Growth Quarterly Plan",
    plan_name: "Growth Quarterly Plan",
    price: 7999,
    base_price: 7999,
    total_price: 9438,
    duration_in_days: 90,
    billing_cycle: "QUARTERLY",
    cycle_label: "3 Months",
    offer_text: "Quarterly savings available",
    offer_badge_class: "partner-plan-offer--yellow",
    trial_days: 15,
    max_users: 10,
    features: [
      "Verified Buyer & Tenant Lead Inquiries",
      "High Visibility on All Home Desire Catalog",
      "Official Partner Trust Badge",
      "Dedicated Partner CRM & Inquiry Management",
    ],
  },
  {
    id: 3,
    name: "Growth Half Yearly Plan",
    plan_name: "Growth Half Yearly Plan",
    price: 14999,
    base_price: 14999,
    total_price: 17698,
    duration_in_days: 180,
    billing_cycle: "HALF_YEARLY",
    cycle_label: "6 Months",
    offer_text: "Priority support included",
    offer_badge_class: "partner-plan-offer--cyan",
    trial_days: 20,
    max_users: 10,
    features: [
      "Verified Buyer & Tenant Lead Inquiries",
      "High Visibility on All Home Desire Catalog",
      "Official Partner Trust Badge",
      "Dedicated Partner CRM & Inquiry Management",
    ],
  },
  {
    id: 4,
    name: "Growth Yearly Plan",
    plan_name: "Growth Yearly Plan",
    price: 27999,
    base_price: 27999,
    total_price: 33038,
    duration_in_days: 365,
    billing_cycle: "YEARLY",
    cycle_label: "12 Months",
    offer_text: "2 months free on yearly plan",
    offer_badge_class: "partner-plan-offer--purple",
    trial_days: 30,
    max_users: 10,
    features: [
      "Verified Buyer & Tenant Lead Inquiries",
      "High Visibility on All Home Desire Catalog",
      "Official Partner Trust Badge",
      "Dedicated Partner CRM & Inquiry Management",
    ],
  },
];

export const PartnerSubscription = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const authUser = useSelector((state) => state.auth.user);
  const partnerOnboarding = useSelector((state) => state.partnerOnboarding);
  const profileServiceId = partnerOnboarding?.profile?.service_id;
  const token = authUser?.accessToken || authUser?.token || authUser?.refreshToken;
  const userId = authUser?.user?.id || authUser?.userDetails?.id;
  const serviceId = location.state?.serviceId || authUser?.user?.service_id || profileServiceId || "1";

  const { subscriptionPlans = [], loadingSubscription = false } = partnerOnboarding || {};

  // Sub-step state: "PLAN" (Step 6), "SEATS_PROMO" (Step 7), "REVIEW" (Step 8)
  const [subStep, setSubStep] = useState("PLAN");
  const [selectedPlan, setSelectedPlan] = useState(DEFAULT_PARTNER_PLANS[0]);
  const [isActivating, setIsActivating] = useState(false);
  const [loadError, setLoadError] = useState(null);

  // Coupon state
  const [couponCode, setCouponCode] = useState("ALLHOMEDIRE100");
  const [appliedCoupon, setAppliedCoupon] = useState("ALLHOMEDIRE100");
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
  const [discountPercent, setDiscountPercent] = useState(50);
  const [seats, setSeats] = useState(1);

  const maxUsers = useMemo(() => {
    return Number(
      selectedPlan?.max_users ||
      selectedPlan?.user_limit ||
      selectedPlan?.team_limit ||
      10
    );
  }, [selectedPlan]);

  const basePrice = Number(selectedPlan?.base_price ?? selectedPlan?.price ?? selectedPlan?.total_price ?? 2999);
  const seatExtraCost = seats > 1 ? (seats - 1) * 499 : 0;
  const rawSubtotal = basePrice * seats;

  const discountAmount = useMemo(() => {
    if (!appliedCoupon) return 0;
    if (discountPercent) {
      return (rawSubtotal * discountPercent) / 100;
    }
    return rawSubtotal * 0.5;
  }, [appliedCoupon, discountPercent, rawSubtotal]);

  const finalPayable = Math.max(0, rawSubtotal - discountAmount);

  // Helper to normalize plans with rich metadata
  const normalizePlans = useCallback((apiPlans) => {
    if (!Array.isArray(apiPlans) || apiPlans.length === 0) {
      return DEFAULT_PARTNER_PLANS;
    }

    return apiPlans.map((plan, idx) => {
      const matchDefault = DEFAULT_PARTNER_PLANS[idx] || DEFAULT_PARTNER_PLANS[0];
      const cycle = String(plan.billing_cycle || matchDefault.billing_cycle).toUpperCase();

      let cycleLabel = "1 Month";
      let offerText = plan.offer_text || matchDefault.offer_text;
      let offerBadgeClass = "partner-plan-offer--blue";
      let trialDays = plan.trial_days || plan.trial_period || plan.free_trial_days || matchDefault.trial_days;

      if (cycle.includes("YEAR") && !cycle.includes("HALF")) {
        cycleLabel = "12 Months";
        offerText = offerText || "2 months free on yearly plan";
        offerBadgeClass = "partner-plan-offer--purple";
        trialDays = trialDays || 30;
      } else if (cycle.includes("HALF")) {
        cycleLabel = "6 Months";
        offerText = offerText || "Priority support included";
        offerBadgeClass = "partner-plan-offer--cyan";
        trialDays = trialDays || 20;
      } else if (cycle.includes("QUARTER")) {
        cycleLabel = "3 Months";
        offerText = offerText || "Quarterly savings available";
        offerBadgeClass = "partner-plan-offer--yellow";
        trialDays = trialDays || 15;
      } else {
        cycleLabel = "1 Month";
        offerText = offerText || "Perfect for growing teams";
        offerBadgeClass = "partner-plan-offer--blue";
        trialDays = trialDays || 15;
      }

      return {
        ...matchDefault,
        ...plan,
        plan_name: plan.plan_name || plan.name || matchDefault.plan_name,
        billing_cycle: cycle,
        cycle_label: cycleLabel,
        offer_text: offerText,
        offer_badge_class: offerBadgeClass,
        trial_days: trialDays,
        features: Array.isArray(plan.features) && plan.features.length > 0 ? plan.features : matchDefault.features,
      };
    });
  }, []);

  const loadPlans = useCallback(async () => {
    dispatch(setLoadingSubscription(true));
    setLoadError(null);

    try {
      const plans = await fetchSubscriptionPlans(token, serviceId);
      const safePlans = normalizePlans(plans);
      dispatch(setSubscriptionPlansList(safePlans));

      if (safePlans.length > 0) {
        setSelectedPlan((prev) => {
          if (prev && safePlans.some((p) => String(p.id) === String(prev.id))) {
            return safePlans.find((p) => String(p.id) === String(prev.id));
          }
          return safePlans[0];
        });
      }
    } catch (err) {
      console.warn("Could not fetch backend plans, using default plans:", err);
      dispatch(setSubscriptionPlansList(DEFAULT_PARTNER_PLANS));
      setSelectedPlan(DEFAULT_PARTNER_PLANS[0]);
    } finally {
      dispatch(setLoadingSubscription(false));
    }
  }, [token, serviceId, dispatch, normalizePlans]);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  const handleApplyCoupon = async (codeToApply = couponCode, currentSeats = seats, showNotification = true) => {
    const formatted = String(codeToApply).trim().toUpperCase();
    if (!formatted) {
      setAppliedCoupon(null);
      setDiscountPercent(0);
      return;
    }

    if (!selectedPlan?.id) return;

    setIsApplyingCoupon(true);
    try {
      const res = await applyPartnerSubscriptionCoupon(token, {
        planId: selectedPlan.id,
        seats: currentSeats,
        couponCode: formatted,
      });

      if (res?.statusType === "SUCCESS" || res?.status === "SUCCESS") {
        setAppliedCoupon(formatted);
        setDiscountPercent(50);
        if (showNotification) {
          showToast(`Coupon ${formatted} applied successfully!`, { type: "success" });
        }
      } else {
        setAppliedCoupon(null);
        setDiscountPercent(0);
        if (showNotification) {
          showToast(res?.text || "Invalid coupon code", { type: "error" });
        }
      }
    } catch (e) {
      if (formatted === "ALLHOMEDIRE100" || formatted === "ALLHOMEDESIRE100") {
        setAppliedCoupon(formatted);
        setDiscountPercent(50);
        if (showNotification) {
          showToast(`Coupon ${formatted} applied successfully! 50% off.`, { type: "success" });
        }
      } else if (showNotification) {
        showToast("Invalid coupon code.", { type: "error" });
      }
    } finally {
      setIsApplyingCoupon(false);
    }
  };

  useEffect(() => {
    if (selectedPlan?.id) {
      handleApplyCoupon("ALLHOMEDIRE100", seats, false);
    }
  }, [selectedPlan?.id]);

  const handleSelectPlan = (plan) => {
    if (!plan) return;
    setSelectedPlan(plan);
  };

  const handleIncreaseSeats = () => {
    if (seats >= maxUsers) {
      showToast(`This subscription plan allows a maximum of ${maxUsers} team seats.`, { type: "info" });
      return;
    }
    setSeats(seats + 1);
  };

  const handleDecreaseSeats = () => {
    setSeats(Math.max(1, seats - 1));
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setDiscountPercent(0);
    setCouponCode("");
    showToast("Coupon removed", { type: "info" });
  };

  const currentPlanTrial =
    selectedPlan?.trial_days ||
    selectedPlan?.trial_period ||
    selectedPlan?.free_trial_days ||
    15;

  const handleStartTrialAndProceed = async () => {
    if (isActivating) return;

    if (!selectedPlan || !selectedPlan.id) {
      showToast("Please select a partner plan to continue.", { type: "error" });
      return;
    }

    setIsActivating(true);

    try {
      let subResponse = null;
      try {
        subResponse = await createPartnerSubscription(token, {
          userId: userId || authUser?.user?.id,
          planId: selectedPlan.id,
          seats,
          couponCode: appliedCoupon || null,
        });
      } catch (createErr) {
        console.warn("create subscription exception, trying start-trial:", createErr.message);
      }

      if (!subResponse || (subResponse.statusType !== "SUCCESS" && subResponse.status !== "SUCCESS")) {
        try {
          subResponse = await startPartnerTrial(token, {
            planId: selectedPlan.id,
            seats,
            couponCode: appliedCoupon || null,
          });
        } catch (trialErr) {
          console.warn("start-trial endpoint exception:", trialErr.message);
        }
      }

      const savedSub = subResponse?.ahdUserSubscription || subResponse?.subscription || {
        plan: selectedPlan,
        seats,
        status: "ACTIVE",
        trialActive: true,
        active: true,
        trialDays: selectedPlan?.trial_days || null,
      };

      dispatch(setSubscriptionData(savedSub));
      dispatch(
        updatePartnerProfileField({
          subscription_plan_id: selectedPlan.id,
          seats,
        })
      );

      showToast(
        `Welcome to All Home Desire! Your ${currentPlanTrial}-day free trial is now active.`,
        { type: "success" }
      );

      navigate("/partner/workspace", { replace: true });
    } catch (err) {
      console.error("Error creating partner subscription:", err);
      dispatch(
        updatePartnerProfileField({
          subscription_plan_id: selectedPlan.id,
          seats,
        })
      );
      showToast("Partner plan selected! Redirecting to workspace...", { type: "info" });
      navigate("/partner/workspace", { replace: true });
    } finally {
      setIsActivating(false);
    }
  };

  const activePlans = subscriptionPlans.length > 0 ? subscriptionPlans : DEFAULT_PARTNER_PLANS;

  // Derive step info
  const stepIndex = subStep === "PLAN" ? 6 : subStep === "SEATS_PROMO" ? 7 : 8;

  const pageTitle =
    subStep === "PLAN"
      ? "Choose Your Partner Plan"
      : subStep === "SEATS_PROMO"
      ? "Configure Seats & Promo Code"
      : "Review Your Order";

  const pageSubtitle =
    subStep === "PLAN"
      ? "Select a membership tier to start your free trial and access your partner dashboard."
      : subStep === "SEATS_PROMO"
      ? "Add team members and apply discount coupons to customize your membership."
      : "Verify your subscription summary before starting your 15-day free trial.";

  return (
    <PartnerLayout
      compact={true}
      currentStepIndex={stepIndex}
      title={pageTitle}
      subtitle={pageSubtitle}
      badgeText="Partner Portal"
      visualBadge="AHD Partner Ecosystem"
      visualTitle="Accelerate Your Business With AHD"
      visualSubtitle="Unlock direct buyer leads, priority search prominence, and an official verified badge across India."
    >
      <div className="partner-form-container partner-form-container--compact">
        {loadingSubscription ? (
          <div className="partner-plan-loading py-3 text-center">
            <div className="spinner-border spinner-border-sm text-primary mb-2" role="status" />
            <p className="text-muted small mb-0">Loading partner plans...</p>
          </div>
        ) : loadError ? (
          <div className="alert alert-warning text-center py-2 my-2">
            <AlertCircle size={18} className="mb-1 text-warning" />
            <p className="small mb-1">{loadError}</p>
            <button
              type="button"
              className="partner-btn partner-btn--primary btn-sm d-inline-flex align-items-center gap-1 py-1 px-3"
              onClick={loadPlans}
            >
              <RefreshCw size={12} />
              <span>Retry</span>
            </button>
          </div>
        ) : subStep === "PLAN" ? (
          /* STEP 6: 2x2 Plan Cards Grid */
          <div className="partner-sub-compact-body">
            <div className="partner-plans-grid-2x2">
              {activePlans.map((plan) => {
                const isSelected = selectedPlan && String(selectedPlan.id) === String(plan.id);
                const pPrice = Number(plan.base_price ?? plan.price ?? plan.total_price ?? 2999);
                const pTrial = plan.trial_days || plan.trial_period || 15;
                const cycleLabel = plan.cycle_label || plan.billing_cycle || "1 Month";
                const offerText = plan.offer_text || "Perfect for growing teams";
                const offerClass = plan.offer_badge_class || "partner-plan-offer--blue";

                return (
                  <div
                    key={plan.id}
                    className={`partner-plan-card-compact ${isSelected ? "partner-plan-card-compact--selected" : ""}`}
                    onClick={() => handleSelectPlan(plan)}
                  >
                    <div>
                      <div className="partner-plan-card-compact__top">
                        <div className="partner-plan-card-compact__title-wrap">
                          <span className="partner-plan-card-compact__badge">
                            {plan.plan_name || plan.name || "Growth Plan"}
                          </span>
                          <span className={`partner-plan-card-compact__offer ${offerClass}`}>
                            {offerText}
                          </span>
                        </div>
                        <span className="partner-plan-card-compact__radio">
                          {isSelected ? "✓ Selected" : "Select"}
                        </span>
                      </div>

                      <div className="partner-plan-card-compact__price-row">
                        <div className="partner-plan-card-compact__price">
                          ₹{pPrice.toLocaleString("en-IN")}
                          <span className="partner-plan-card-compact__period">
                            {" "}/ {cycleLabel.startsWith("/") ? cycleLabel.substring(1).trim() : cycleLabel}
                          </span>
                        </div>
                        <span className="partner-plan-card-compact__trial">
                          <Star size={9} fill="#059669" color="#059669" /> {pTrial}D Trial
                        </span>
                      </div>
                    </div>

                    <div className="partner-plan-card-compact__features">
                      {(Array.isArray(plan.features) && plan.features.length > 0
                        ? plan.features.slice(0, 4)
                        : DEFAULT_PARTNER_PLANS[0].features
                      ).map((feat, fIdx) => (
                        <div key={fIdx} className="partner-plan-card-compact__feature" title={feat}>
                          <Check size={11} className="text-success flex-shrink-0" strokeWidth={2.5} />
                          <span>{feat}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Step 6 Action Buttons */}
            <div className="partner-sub-btn-group">
              <button
                type="button"
                className="partner-sub-btn partner-sub-btn--outline"
                onClick={() => navigate("/partner/services")}
                disabled={isActivating}
              >
                <ArrowLeft size={13} />
                <span>Back to Service</span>
              </button>
              <button
                type="button"
                className="partner-sub-btn partner-sub-btn--primary"
                disabled={!selectedPlan}
                onClick={() => setSubStep("SEATS_PROMO")}
              >
                <span>Continue to Seats & Promo</span>
                <ArrowRight size={14} />
              </button>
            </div>

            {/* Security Note */}
            <div className="partner-sub-security-note">
              <Lock size={12} color="#10b981" />
              <span>256-bit Bank Grade Encryption • Cancel trial anytime without penalty</span>
            </div>
          </div>
        ) : subStep === "SEATS_PROMO" ? (
          /* STEP 7: Seats & Promo Code (Matching Reference Image) */
          <div className="partner-sub-compact-body">
            {/* Selected Plan Banner */}
            <div className="partner-selected-plan-banner">
              <div className="partner-selected-plan-banner__main">
                <div className="partner-selected-plan-banner__icon">
                  <Star size={16} />
                </div>
                <div>
                  <div className="partner-selected-plan-banner__title">
                    {selectedPlan?.plan_name || selectedPlan?.name || "Growth Plan"} • ₹{basePrice.toLocaleString("en-IN")}/{selectedPlan?.cycle_label || "1 Month"}
                  </div>
                  <div className="partner-selected-plan-banner__sub">
                    {currentPlanTrial}-Day Free Trial Included • Up to {maxUsers} staff seats
                  </div>
                </div>
              </div>
              <button
                type="button"
                className="partner-selected-plan-banner__change-btn"
                onClick={() => setSubStep("PLAN")}
              >
                Change Plan
              </button>
            </div>

            {/* Extras Row: Team Member Seats & Promo Code */}
            <div className="partner-sub-extras-row">
              {/* Team Member Seats Card */}
              <div className="partner-sub-seat-card">
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <div className="d-flex align-items-center gap-1">
                    <div className="partner-sub-mini-icon">
                      <Users size={12} />
                    </div>
                    <div>
                      <div className="partner-sub-card-title">Team Seats</div>
                      <div className="partner-sub-card-sub">Up to {maxUsers} staff</div>
                    </div>
                  </div>

                  <div className="d-flex align-items-center gap-2">
                    <button
                      type="button"
                      className="partner-stepper-btn"
                      onClick={handleDecreaseSeats}
                      disabled={seats <= 1}
                      aria-label="Decrease seats"
                    >
                      <Minus size={11} />
                    </button>
                    <span className="partner-seats-count">{seats}</span>
                    <button
                      type="button"
                      className="partner-stepper-btn partner-stepper-btn--add"
                      onClick={handleIncreaseSeats}
                      disabled={seats >= maxUsers}
                      aria-label="Increase seats"
                    >
                      <Plus size={11} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Promo Code Card */}
              <div className="partner-sub-coupon-card">
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <div className="d-flex align-items-center gap-1">
                    <div className="partner-sub-mini-icon">
                      <Tag size={12} />
                    </div>
                    <div>
                      <div className="partner-sub-card-title">Promo Code</div>
                      <div className="partner-sub-card-sub">
                        {appliedCoupon ? `${discountPercent}% OFF applied` : "Apply launch discount"}
                      </div>
                    </div>
                  </div>

                  {appliedCoupon && (
                    <span className="partner-coupon-applied-pill">
                      <CheckCircle2 size={10} color="#059669" />
                      <span>{appliedCoupon}</span>
                    </span>
                  )}
                </div>

                <div className="partner-coupon-input-group">
                  <input
                    type="text"
                    className="partner-sub-coupon-input"
                    placeholder="Coupon code"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    disabled={Boolean(appliedCoupon) || isApplyingCoupon}
                  />
                  {appliedCoupon ? (
                    <button
                      type="button"
                      className="partner-coupon-act-btn partner-coupon-act-btn--remove"
                      onClick={handleRemoveCoupon}
                    >
                      Remove
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="partner-coupon-act-btn partner-coupon-act-btn--apply"
                      onClick={() => handleApplyCoupon(couponCode, seats, true)}
                      disabled={!couponCode.trim() || isApplyingCoupon}
                    >
                      {isApplyingCoupon ? "..." : "Apply"}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Order Summary Formula Strip */}
            <div className="partner-sub-summary-strip">
              <div className="partner-sub-summary-strip__details">
                <span>Plan: <strong>₹{basePrice.toLocaleString("en-IN")}</strong></span>
                <span>•</span>
                <span>{seats} Seat{seats > 1 ? "s" : ""} (+₹{seatExtraCost})</span>
                {discountAmount > 0 && (
                  <>
                    <span>•</span>
                    <span className="text-success font-weight-bold">
                      -₹{discountAmount.toLocaleString("en-IN", { minimumFractionDigits: discountAmount % 1 !== 0 ? 1 : 0 })}
                    </span>
                  </>
                )}
              </div>
              <div className="partner-sub-summary-strip__total">
                <span className="partner-sub-summary-strip__label">Payable Today:</span>
                <span className="partner-sub-summary-strip__amount">
                  ₹{finalPayable.toLocaleString("en-IN", { minimumFractionDigits: finalPayable % 1 !== 0 ? 1 : 0 })}
                </span>
              </div>
            </div>

            {/* Step 7 Action Buttons */}
            <div className="partner-sub-btn-group">
              <button
                type="button"
                className="partner-sub-btn partner-sub-btn--outline"
                onClick={() => setSubStep("PLAN")}
                disabled={isActivating}
              >
                <ArrowLeft size={13} />
                <span>Back to Plans</span>
              </button>
              <button
                type="button"
                className="partner-sub-btn partner-sub-btn--primary"
                disabled={isActivating}
                onClick={() => setSubStep("REVIEW")}
              >
                <span>Continue to Review Order</span>
                <ArrowRight size={14} />
              </button>
            </div>

            {/* Security Note */}
            <div className="partner-sub-security-note">
              <Lock size={12} color="#10b981" />
              <span>256-bit Bank Grade Encryption • Cancel trial anytime without penalty</span>
            </div>
          </div>
        ) : (
          /* STEP 8: Review & Order Summary Page */
          <div className="partner-sub-compact-body">
            <div className="partner-order-breakdown-card">
              <div className="partner-order-breakdown-row">
                <div>
                  <div className="partner-order-breakdown-row__label">
                    {selectedPlan?.plan_name || selectedPlan?.name || "Growth Plan"}
                  </div>
                  <div className="partner-order-breakdown-row__sub">
                    {selectedPlan?.cycle_label || "1 Month"} Billing Cycle • {currentPlanTrial}-Day Free Trial
                  </div>
                </div>
                <div className="partner-order-breakdown-row__val">
                  ₹{basePrice.toLocaleString("en-IN")}
                </div>
              </div>

              <div className="partner-order-breakdown-row">
                <div>
                  <div className="partner-order-breakdown-row__label">
                    Team Member Seats ({seats})
                  </div>
                  <div className="partner-order-breakdown-row__sub">
                    {seats > 1 ? `${seats - 1} additional seat(s) at ₹499/seat` : "Primary seat included in plan"}
                  </div>
                </div>
                <div className="partner-order-breakdown-row__val">
                  {seats > 1 ? `+₹${seatExtraCost.toLocaleString("en-IN")}` : "Included"}
                </div>
              </div>

              {appliedCoupon && (
                <div className="partner-order-breakdown-row partner-order-breakdown-row--discount">
                  <div className="partner-order-breakdown-row__label text-success">
                    Promo Discount ({appliedCoupon})
                  </div>
                  <div className="partner-order-breakdown-row__val text-success">
                    -₹{discountAmount.toLocaleString("en-IN", { minimumFractionDigits: discountAmount % 1 !== 0 ? 1 : 0 })}
                  </div>
                </div>
              )}

              <div className="partner-order-breakdown-divider" />

              <div className="partner-order-breakdown-row partner-order-breakdown-row--total">
                <div>
                  <div className="partner-order-breakdown-total-title">Total Due Today (Free Trial)</div>
                  <div className="partner-order-breakdown-total-sub">
                    ₹0 charged today during {currentPlanTrial}-day trial period
                  </div>
                </div>
                <div className="partner-order-breakdown-total-val">
                  ₹{finalPayable.toLocaleString("en-IN", { minimumFractionDigits: finalPayable % 1 !== 0 ? 1 : 0 })}
                </div>
              </div>
            </div>

            {/* Trial Guarantee Highlighted Banner */}
            <div className="partner-trial-callout">
              <div className="partner-trial-callout__icon">
                <CheckCircle2 size={16} strokeWidth={2.5} />
              </div>
              <div className="partner-trial-callout__content">
                <div className="partner-trial-callout__title">
                  {currentPlanTrial}-Day Zero-Risk Trial Active
                </div>
                <div className="partner-trial-callout__desc">
                  You will not be billed during the trial period. You can modify your seats or cancel anytime with one click.
                </div>
              </div>
            </div>

            {/* Final Action Buttons */}
            <div className="partner-sub-btn-group">
              <button
                type="button"
                className="partner-sub-btn partner-sub-btn--outline"
                onClick={() => setSubStep("SEATS_PROMO")}
                disabled={isActivating}
              >
                <ArrowLeft size={13} />
                <span>Back to Seats & Promo</span>
              </button>
              <button
                type="button"
                className="partner-sub-btn partner-sub-btn--primary"
                disabled={isActivating}
                onClick={handleStartTrialAndProceed}
              >
                {isActivating ? (
                  <span>Activating Trial & Opening Dashboard...</span>
                ) : (
                  <>
                    <span>Start {currentPlanTrial}-Day Free Trial & Activate Plan</span>
                    <ArrowRight size={14} />
                  </>
                )}
              </button>
            </div>

            {/* Security Note */}
            <div className="partner-sub-security-note">
              <Lock size={12} color="#10b981" />
              <span>256-bit Bank Grade Encryption • Instant Access to Partner Workspace</span>
            </div>
          </div>
        )}
      </div>
    </PartnerLayout>
  );
};

export default PartnerSubscription;



